// src/data/questions.ts

export interface AnswerOption {
  label: string;
  score: number;
}

export interface Question {
  id: number;
  questionText: string;
  options: AnswerOption[];
}

export interface ResultLevel {
  min: number;
  max: number;
  title: string;
  description: string;
  color: string;
}

// Thang điểm đánh giá mới (Tổng điểm tối đa: 42 * 3 = 126)
export const results: ResultLevel[] = [
  {
    min: 0,
    max: 80,
    title: "EQ Cần Cải Thiện",
    description:
      "Bạn có xu hướng để cảm xúc chi phối hành động. Hãy tập trung vào việc nhận diện cảm xúc của mình và lắng nghe người khác nhiều hơn.",
    color: "error", // Đỏ
  },
  {
    min: 81,
    max: 105,
    title: "EQ Khá",
    description:
      "Bạn có khả năng quản lý cảm xúc tốt trong hầu hết các tình huống, nhưng đôi khi áp lực lớn vẫn khiến bạn mất cân bằng.",
    color: "warning", // Vàng cam
  },
  {
    min: 106,
    max: 126,
    title: "EQ Xuất Sắc",
    description:
      "Tuyệt vời! Bạn cực kỳ nhạy bén với cảm xúc của bản thân và người khác. Bạn là người giao tiếp tốt và có khả năng lãnh đạo bẩm sinh.",
    color: "success", // Xanh lá
  },
];

export const questions: Question[] = [
  // --- PHẦN 1: NHẬN THỨC BẢN THÂN (Self-Awareness) ---
  {
    id: 1,
    questionText: "Khi bạn cảm thấy buồn mà không rõ lý do, bạn sẽ làm gì?",
    options: [
      { label: "Lờ nó đi và hy vọng nó tự hết.", score: 1 },
      { label: "Cố gắng làm việc khác để phân tâm.", score: 2 },
      {
        label: "Dành thời gian suy nghĩ xem điều gì đã kích hoạt cảm xúc này.",
        score: 3,
      },
    ],
  },
  {
    id: 2,
    questionText:
      "Bạn nhận ra mình mắc lỗi nghiêm trọng trong công việc. Phản ứng đầu tiên là?",
    options: [
      { label: "Lo sợ sếp biết và tìm cách che giấu.", score: 1 },
      { label: "Cảm thấy tội lỗi và lo lắng cả ngày.", score: 2 },
      {
        label: "Chấp nhận lỗi sai và lập tức tìm giải pháp khắc phục.",
        score: 3,
      },
    ],
  },
  {
    id: 3,
    questionText: "Khi ai đó nhận xét về điểm yếu của bạn:",
    options: [
      { label: "Tức giận và cho rằng họ đang ghen tị.", score: 1 },
      { label: "Cảm thấy tự ti và buồn bã.", score: 2 },
      {
        label: "Ghi nhận và xem xét liệu đó có phải sự thật để cải thiện.",
        score: 3,
      },
    ],
  },
  {
    id: 4,
    questionText:
      "Bạn có thường xuyên biết rõ mình đang cảm thấy gì tại một thời điểm cụ thể không?",
    options: [
      { label: "Hiếm khi, tôi thường lẫn lộn các cảm xúc.", score: 1 },
      { label: "Thỉnh thoảng, khi cảm xúc đó rất mạnh.", score: 2 },
      { label: "Thường xuyên, tôi gọi tên được cảm xúc của mình.", score: 3 },
    ],
  },
  {
    id: 5,
    questionText: "Khi thành công trong một dự án, bạn nghĩ gì?",
    options: [
      { label: "Do mình may mắn thôi.", score: 1 },
      { label: "Vui mừng nhưng không suy nghĩ nhiều.", score: 2 },
      {
        label:
          "Nhận biết rõ kỹ năng nào của mình đã đóng góp vào thành công đó.",
        score: 3,
      },
    ],
  },
  {
    id: 6,
    questionText: "Cơ thể bạn phản ứng thế nào khi bạn tức giận?",
    options: [
      { label: "Tôi không để ý lắm.", score: 1 },
      { label: "Tôi chỉ biết là mình đang nóng mặt.", score: 2 },
      {
        label:
          "Tôi nhận ra tim đập nhanh, tay nắm chặt hoặc giọng nói thay đổi ngay lập tức.",
        score: 3,
      },
    ],
  },
  {
    id: 7,
    questionText:
      "Bạn có biết điều gì thường khiến bạn mất bình tĩnh nhất không?",
    options: [
      { label: "Không, nó xảy ra ngẫu nhiên.", score: 1 },
      { label: "Mơ hồ biết vài thứ.", score: 2 },
      { label: "Rất rõ, tôi biết các 'nút bấm' cảm xúc của mình.", score: 3 },
    ],
  },
  {
    id: 8,
    questionText: "Khi phải đưa ra quyết định quan trọng, bạn dựa vào đâu?",
    options: [
      { label: "Cảm tính nhất thời.", score: 1 },
      { label: "Chỉ dựa trên số liệu logic khô khan.", score: 2 },
      {
        label: "Kết hợp giữa phân tích logic và trực giác của bản thân.",
        score: 3,
      },
    ],
  },

  // --- PHẦN 2: KIỂM SOÁT BẢN THÂN (Self-Regulation) ---
  {
    id: 9,
    questionText: "Đang lái xe thì có người tạt đầu bạn. Bạn sẽ:",
    options: [
      { label: "Bấm còi inh ỏi và chửi thề.", score: 1 },
      { label: "Bực mình nhưng im lặng chịu đựng.", score: 2 },
      {
        label: "Hít thở sâu và nghĩ rằng có thể họ đang vội việc gấp.",
        score: 3,
      },
    ],
  },
  {
    id: 10,
    questionText: "Đồng nghiệp làm hỏng việc khiến bạn bị liên lụy. Bạn sẽ:",
    options: [
      { label: "La mắng họ ngay trước mặt mọi người.", score: 1 },
      { label: "Phàn nàn với người khác về sự kém cỏi của họ.", score: 2 },
      {
        label: "Trao đổi riêng thẳng thắn để tìm cách giải quyết hậu quả.",
        score: 3,
      },
    ],
  },
  {
    id: 11,
    questionText: "Bạn đang rất stress vì deadline. Bạn sẽ:",
    options: [
      { label: "Nổi cáu với bất kỳ ai làm phiền mình.", score: 1 },
      { label: "Cố gắng làm việc xuyên đêm bất chấp sức khỏe.", score: 2 },
      {
        label: "Nghỉ ngơi ngắn, sắp xếp lại ưu tiên và làm từng việc một.",
        score: 3,
      },
    ],
  },
  {
    id: 12,
    questionText: "Khi kế hoạch đi chơi bị hủy phút chót do trời mưa:",
    options: [
      { label: "Cả ngày hôm đó của tôi bị hủy hoại, tôi rất bực.", score: 1 },
      { label: "Thất vọng và nằm lướt điện thoại cho qua giờ.", score: 2 },
      {
        label: "Nhanh chóng tìm một hoạt động thú vị khác trong nhà.",
        score: 3,
      },
    ],
  },
  {
    id: 13,
    questionText: "Trong một cuộc tranh luận gay gắt, bạn nhận ra mình sai:",
    options: [
      { label: "Cố cãi chày cãi cối để bảo vệ danh dự.", score: 1 },
      { label: "Im lặng và lảng tránh vấn đề.", score: 2 },
      { label: "Thừa nhận mình sai và xin lỗi ngay lúc đó.", score: 3 },
    ],
  },
  {
    id: 14,
    questionText: "Bạn muốn mua một món đồ đắt tiền nhưng chưa đủ tiền:",
    options: [
      { label: "Vay mượn hoặc quẹt thẻ tín dụng mua ngay.", score: 1 },
      { label: "Cảm thấy khó chịu vì không có được nó.", score: 2 },
      { label: "Lên kế hoạch tiết kiệm để mua nó sau.", score: 3 },
    ],
  },
  {
    id: 15,
    questionText: "Khi bị khiêu khích trên mạng xã hội:",
    options: [
      { label: "Lao vào 'chiến' ngay lập tức.", score: 1 },
      { label: "Block ngay nhưng trong lòng vẫn tức.", score: 2 },
      { label: "Bỏ qua vì biết tranh luận đó vô bổ.", score: 3 },
    ],
  },
  {
    id: 16,
    questionText:
      "Bạn phản ứng thế nào với sự thay đổi bất ngờ trong công việc?",
    options: [
      { label: "Hoảng loạn và phản đối kịch liệt.", score: 1 },
      { label: "Lo lắng và thụ động chờ hướng dẫn.", score: 2 },
      {
        label: "Bình tĩnh thích nghi và tìm cơ hội trong sự thay đổi.",
        score: 3,
      },
    ],
  },

  // --- PHẦN 3: ĐỘNG LỰC (Motivation) ---
  {
    id: 17,
    questionText: "Bạn gặp thất bại trong một dự án khởi nghiệp/cá nhân:",
    options: [
      { label: "Từ bỏ và không bao giờ thử lại.", score: 1 },
      { label: "Cần một thời gian dài để hồi phục tinh thần.", score: 2 },
      { label: "Xem đó là bài học và bắt đầu lại thông minh hơn.", score: 3 },
    ],
  },
  {
    id: 18,
    questionText: "Thái độ của bạn đối với những công việc nhàm chán:",
    options: [
      { label: "Làm qua loa cho xong chuyện.", score: 1 },
      { label: "Làm đủ trách nhiệm được giao.", score: 2 },
      {
        label: "Cố gắng tìm niềm vui hoặc cách cải tiến để làm nó tốt hơn.",
        score: 3,
      },
    ],
  },
  {
    id: 19,
    questionText: "Bạn đặt mục tiêu cho bản thân như thế nào?",
    options: [
      { label: "Tôi hiếm khi đặt mục tiêu cụ thể.", score: 1 },
      { label: "Tôi đặt mục tiêu dễ dàng để chắc chắn đạt được.", score: 2 },
      {
        label: "Tôi đặt mục tiêu thách thức nhưng thực tế để phát triển.",
        score: 3,
      },
    ],
  },
  {
    id: 20,
    questionText: "Điều gì thúc đẩy bạn làm việc mỗi ngày?",
    options: [
      { label: "Sợ bị đuổi việc hoặc không có tiền.", score: 1 },
      { label: "Sự công nhận của người khác.", score: 2 },
      { label: "Niềm đam mê và mong muốn hoàn thiện bản thân.", score: 3 },
    ],
  },
  {
    id: 21,
    questionText: "Khi đối mặt với một nhiệm vụ cực khó:",
    options: [
      { label: "Tôi tìm lý do để trì hoãn.", score: 1 },
      { label: "Tôi làm nhưng lo sợ thất bại.", score: 2 },
      { label: "Tôi hào hứng vì đây là cơ hội để học hỏi.", score: 3 },
    ],
  },
  {
    id: 22,
    questionText: "Bạn có lạc quan về tương lai không?",
    options: [
      { label: "Không, cuộc sống đầy rủi ro.", score: 1 },
      { label: "Bình thường, sao cũng được.", score: 2 },
      { label: "Có, tôi tin mình có thể vượt qua các trở ngại.", score: 3 },
    ],
  },
  {
    id: 23,
    questionText:
      "Để đạt được mục tiêu dài hạn, bạn có sẵn sàng hy sinh thú vui trước mắt?",
    options: [
      { label: "Không, tôi sống cho hiện tại.", score: 1 },
      { label: "Khó khăn lắm tôi mới làm được.", score: 2 },
      { label: "Sẵn sàng, tôi có tính kỷ luật cao.", score: 3 },
    ],
  },
  {
    id: 24,
    questionText: "Bạn làm gì để duy trì năng lượng tích cực?",
    options: [
      { label: "Chờ đợi người khác đem niềm vui đến.", score: 1 },
      { label: "Xem phim hoặc giải trí đơn thuần.", score: 2 },
      {
        label: "Chủ động tạo ra niềm vui và môi trường tích cực quanh mình.",
        score: 3,
      },
    ],
  },

  // --- PHẦN 4: THẤU CẢM (Empathy) ---
  {
    id: 25,
    questionText: "Bạn thấy một người bạn đang khóc:",
    options: [
      { label: "Tránh xa vì sợ phiền phức.", score: 1 },
      { label: "Đưa khăn giấy và bảo 'Đừng khóc nữa'.", score: 2 },
      { label: "Ngồi xuống bên cạnh và lặng lẽ lắng nghe.", score: 3 },
    ],
  },
  {
    id: 26,
    questionText: "Khi người khác nói chuyện với bạn, bạn thường:",
    options: [
      { label: "Vừa nghe vừa xem điện thoại.", score: 1 },
      { label: "Nghe và chuẩn bị sẵn câu trả lời trong đầu.", score: 2 },
      {
        label: "Tập trung hoàn toàn vào lời nói và ngôn ngữ cơ thể của họ.",
        score: 3,
      },
    ],
  },
  {
    id: 27,
    questionText: "Bạn thấy một nhân viên mới đứng một mình trong bữa tiệc:",
    options: [
      { label: "Kệ họ, ai cũng phải tự hòa nhập thôi.", score: 1 },
      { label: "Cười xã giao nếu chạm mắt.", score: 2 },
      {
        label: "Chủ động đến bắt chuyện và giới thiệu họ với mọi người.",
        score: 3,
      },
    ],
  },
  {
    id: 28,
    questionText:
      "Khi ai đó có quan điểm trái ngược với bạn về chính trị/xã hội:",
    options: [
      { label: "Coi họ là kẻ ngốc.", score: 1 },
      { label: "Ngưng nói chuyện để tránh cãi vã.", score: 2 },
      { label: "Cố gắng hiểu tại sao họ lại có góc nhìn đó.", score: 3 },
    ],
  },
  {
    id: 29,
    questionText:
      "Bạn có dễ dàng nhận ra bầu không khí căng thẳng trong phòng không?",
    options: [
      { label: "Không, cho đến khi ai đó to tiếng.", score: 1 },
      { label: "Thỉnh thoảng tôi mới để ý.", score: 2 },
      { label: "Có, tôi cảm nhận được ngay khi bước vào.", score: 3 },
    ],
  },
  {
    id: 30,
    questionText: "Khi xem một bộ phim buồn, bạn có khóc không?",
    options: [
      { label: "Không bao giờ, chỉ là phim thôi mà.", score: 1 },
      { label: "Đôi khi rơm rớm nước mắt.", score: 2 },
      { label: "Thường xuyên, tôi dễ đồng cảm với nhân vật.", score: 3 },
    ],
  },
  {
    id: 31,
    questionText: "Bạn bè thường tìm đến bạn để:",
    options: [
      { label: "Mượn tiền.", score: 1 },
      { label: "Rủ đi chơi.", score: 2 },
      { label: "Tâm sự và xin lời khuyên.", score: 3 },
    ],
  },
  {
    id: 32,
    questionText: "Bạn thấy một người lạ gặp nạn trên đường:",
    options: [
      { label: "Đi qua nhanh kẻo bị lừa.", score: 1 },
      { label: "Đứng nhìn xem có ai giúp không.", score: 2 },
      { label: "Dừng lại hỏi thăm và hỗ trợ trong khả năng.", score: 3 },
    ],
  },

  // --- PHẦN 5: KỸ NĂNG XÃ HỘI (Social Skills) ---
  {
    id: 33,
    questionText: "Trong làm việc nhóm, bạn thường đóng vai trò gì?",
    options: [
      { label: "Làm theo chỉ đạo và ít đưa ý kiến.", score: 1 },
      { label: "Bảo vệ ý kiến của mình đến cùng.", score: 2 },
      {
        label: "Kết nối mọi người và dung hòa các ý kiến khác biệt.",
        score: 3,
      },
    ],
  },
  {
    id: 34,
    questionText: "Khi cần thuyết phục người khác, bạn sẽ:",
    options: [
      { label: "Ép buộc họ bằng quyền lực hoặc to tiếng.", score: 1 },
      { label: "Năn nỉ họ giúp mình.", score: 2 },
      { label: "Trình bày lợi ích chung và tạo sự đồng thuận.", score: 3 },
    ],
  },
  {
    id: 35,
    questionText: "Bạn giải quyết mâu thuẫn cá nhân như thế nào?",
    options: [
      { label: "Nhắn tin chửi bới hoặc nói xấu sau lưng.", score: 1 },
      { label: "Nhờ người thứ ba can thiệp.", score: 2 },
      {
        label: "Gặp mặt trực tiếp để nói chuyện thẳng thắn và tôn trọng.",
        score: 3,
      },
    ],
  },
  {
    id: 36,
    questionText: "Khả năng lắng nghe phản hồi tiêu cực của bạn:",
    options: [
      { label: "Rất tệ, tôi thường phản ứng gay gắt.", score: 1 },
      { label: "Tôi nghe nhưng sẽ quên ngay.", score: 2 },
      { label: "Tôi cảm ơn và hỏi thêm chi tiết để hiểu rõ hơn.", score: 3 },
    ],
  },
  {
    id: 37,
    questionText: "Khi tham gia một sự kiện networking (kết nối):",
    options: [
      { label: "Tôi đứng một góc và bấm điện thoại.", score: 1 },
      { label: "Tôi chỉ nói chuyện với người quen.", score: 2 },
      { label: "Tôi chủ động giới thiệu bản thân với người mới.", score: 3 },
    ],
  },
  {
    id: 38,
    questionText: "Cách bạn từ chối một lời đề nghị:",
    options: [
      { label: "Im lặng không trả lời.", score: 1 },
      { label: "Bịa ra một lý do nào đó.", score: 2 },
      { label: "Từ chối lịch sự nhưng dứt khoát và chân thành.", score: 3 },
    ],
  },
  {
    id: 39,
    questionText: "Bạn đánh giá thế nào về khả năng hài hước của mình?",
    options: [
      { label: "Tôi thường đùa vô duyên khiến người khác khó chịu.", score: 1 },
      { label: "Bình thường, lúc vui lúc không.", score: 2 },
      {
        label:
          "Tôi biết cách dùng sự hài hước để giảm căng thẳng cho mọi người.",
        score: 3,
      },
    ],
  },
  {
    id: 40,
    questionText: "Khi bạn cần nhờ vả ai đó:",
    options: [
      { label: "Ra lệnh cho họ làm.", score: 1 },
      { label: "Ấp úng mãi không nói nên lời.", score: 2 },
      { label: "Diễn đạt rõ ràng mong muốn và lý do cần giúp đỡ.", score: 3 },
    ],
  },
  {
    id: 41,
    questionText: "Bạn xử lý thế nào khi có tin đồn thất thiệt về mình?",
    options: [
      { label: "Nổi điên và đi tìm người tung tin.", score: 1 },
      { label: "Lo lắng và suy sụp.", score: 2 },
      {
        label:
          "Bình tĩnh đính chính với những người liên quan và bỏ qua dư luận.",
        score: 3,
      },
    ],
  },
  {
    id: 42,
    questionText: "Sau khi hoàn thành bài test này, bạn dự định làm gì?",
    options: [
      { label: "Xem kết quả rồi quên ngay.", score: 1 },
      { label: "Khoe điểm số nếu cao.", score: 2 },
      {
        label: "Sử dụng kết quả để hiểu mình hơn và phát triển bản thân.",
        score: 3,
      },
    ],
  },
];
